import { formatEquation, formatSide } from '../../shared/algebra.ts';
import type { Equation } from '../../shared/algebra.ts';
import { Icon } from './Icon.tsx';

function Terms({ side }: { side: Equation['left'] }) {
  return (
    <>
      {(['x', 'constant'] as const).map((type) => {
        const value = side[type];
        if (value === '0') return null;
        const amount = Number(value);
        const individual =
          Number.isInteger(amount) &&
          Math.abs(amount) <= (type === 'x' ? 5 : 20);
        const count = individual ? Math.abs(amount) : 1;
        const label = individual
          ? `${amount < 0 ? '−' : ''}${type === 'x' ? 'x' : '1'}`
          : value.replace('-', '−') + (type === 'x' ? 'x' : '');
        return (
          <div key={type} className={`term-group ${type}`}>
            {Array.from({ length: count }, (_, i) => (
              <div
                key={`${value}-${i}`}
                className={`term ${type} ${value.startsWith('-') ? 'negative' : ''} ${individual ? '' : 'grouped'}`}
              >
                <span>{label}</span>
                {!individual && <small>group</small>}
              </div>
            ))}
          </div>
        );
      })}
      {side.x === '0' && side.constant === '0' && (
        <span className="zero">0</span>
      )}
    </>
  );
}
export function MathWorld({
  equation,
  original,
  changed,
  onUndo,
  canUndo,
  busy,
}: {
  equation: Equation;
  original: Equation;
  changed: boolean;
  onUndo: () => void;
  canUndo: boolean;
  busy: boolean;
}) {
  return (
    <section className="math-world" aria-labelledby="relationship-title">
      <div className="world-header">
        <span id="relationship-title" className="eyebrow">
          {changed ? 'Your current equation' : 'The relationship'}
        </span>
        <span className="world-tag">
          <Icon name="leaf" /> Signed tokens
        </span>
      </div>
      <div
        className="equation"
        role="img"
        aria-label={formatEquation(equation)}
      >
        <span>{formatSide(equation.left.x, equation.left.constant)}</span>
        <b>=</b>
        <span>{formatSide(equation.right.x, equation.right.constant)}</span>
      </div>
      <div className="token-stage" aria-hidden="true">
        <div className="orbit one" />
        <div className="orbit two" />
        <div className="term-platform">
          <div className="platform-label">LEFT EXPRESSION</div>
          <div className="term-tray">
            <Terms side={equation.left} />
          </div>
        </div>
        <span className="model-equal">=</span>
        <div className="term-platform">
          <div className="platform-label">RIGHT EXPRESSION</div>
          <div className="term-tray">
            <Terms side={equation.right} />
          </div>
        </div>
      </div>
      <div className="legend">
        <span>
          <i className="x-key" />x · unknown value
        </span>
        <span>
          <i className="unit-key" />1 · one unit
        </span>
      </div>
      <p className="model-caption">
        Tiles represent terms. Their size does not tell us x’s value.
      </p>
      <div className="world-footer">
        <span>
          {changed
            ? `Started with ${formatEquation(original)}`
            : 'Two expressions. The same value.'}
        </span>
        <button className="quiet" onClick={onUndo} disabled={!canUndo || busy}>
          <Icon name="undo" />
          Undo
        </button>
      </div>
    </section>
  );
}
